import {ReactElement, ReactNode} from "react";

export enum ResourceType {
    html = "19", // html-dokument
    staticHtml = "23", // static html-dokument
    dibDocumentFormat = "18", // html, men settes på ifrs-ting...
    checklist = "20", // sjekkliste
    documentGenerator = "24", // dokumentgenerator
    pdf = "8", // .pdf
    microsoftWordOld = "1", // .doc
    microsoftWord = "2", // .docx
    microsoftExcelOld = "4", // .xls
    microsoftExcel = "5", // .xlsx
    microsoftPowerpoint = "7", // .pptx
    microsoftExcelMacroEnabledTemplate = "15", // .xltm
    microsoftExcelTemplate = "16", // .xltx
    microsoftExcelMacroEnabled = "17", // .xlsm
    byggekloss = "29",
    error = "500", /// ingen visning
    skatteAbc = "26",
}

export type FileType = Exclude<
    ResourceType,
    | ResourceType.html
    | ResourceType.checklist
    | ResourceType.documentGenerator
    | ResourceType.skatteAbc
    | ResourceType.error
>;

/** A child reference: either an element ID or inline text */
export type DibElementChild = {id: string} | {text: string};

/** A single element in the flat element map */
export interface DibElement {
    name: string;
    attributes: Record<string, string>;
    children: DibElementChild[];
}

export enum RelatedCategories {
    SOURCES = "1",
    INDEX = "2",
    TOOLS = "4",
}
export type DibObject =
    | DibObjectRelated
    | DibObjectXVar
    | DibObjectVarOptionGroup
    | DibObjectVarOption
    | DibObjectXVarList
    | DibObjectXVarOptional
    | DibObjectPointerXVar
    | DibObjectXList
    | DibObjectXSection
    | DibObjectXAlternative
    | DibObjectXAlternatives
    | DibObjectXComment
    | DibObjectXOptional
    | DibObjectLinkdata
    | DibObjectAccline
    | DibObjectTaxline
    | DibObjectTag;

type DibObjectLinkdata = {
    type: "linkdata";
    id: string;
    data: {
        topicId: string;
        id: string;
        type: "auto-link" | "nav-link" | "auto-link-nav" | "preview" | "href";
        endPoint?: number;
        resourceTypeId: number;
        viewType: number;
        topicTypeId: string;
        topicName: string;
        segmentId?: string;
        href?: string;
    };
};

type DibObjectTag = {
    type: "tag";
    id: string;
    data: {
        id: string;
        name: string;
        tagtypeId: string;
        language: string;
    };
};

export type DibObjectAccline = {
    type: "accline";
    id: string;
    data: {
        id: string;
        codeId: string;
        name: string;
        debetcredit: number;
        accId: string;
        typeId: string;
        dataResourceId: string;
        dataId: string;
    };
};
export type DibObjectTaxline = {
    type: "taxline";
    id: string;
    data: {
        id: string;
        taxId: string;
        name: string;
        accId: string;
        dataResourceId: string;
        dataId: string;
    };
};
export type DibObjectRelated = {
    type: "related";
    id: string;
    data: {
        id: string;
        name: string;
        topictypeId: string;
        dataResourceId: string;
        supplierId: string;
        viewtype: string;
        resourceTypeId: ResourceType;
        categoryId: RelatedCategories;
    };
};

type DibObjectXVar = {
    type: "dib-x-var";
    id: string;
    data: {
        id: string;
        name: string;
        comment?: string;
        standard_text?: string;
        options: string[];
        counter?: boolean;
        isTrigger?: boolean;
        userValues: Record<string, unknown>;
        defaultValue?: string;
    };
};

type DibObjectVarOptionGroup = {
    type: "var_option_group";
    id: string;
    data: {
        id: string;
        options: string[];
    };
};

type DibObjectVarOption = {
    type: "var_option";
    id: string;
    data: {
        id: string;
        name: string;
        value: string;
    };
};

type DibObjectXVarList = {
    type: "dib-x-var-list";
    id: string;
    data: {
        name: string;
        options: string[];
        source: string;
        triggers: unknown[];
        userValues: Record<string, unknown>;
        defaultValue: string;
    };
};

type DibObjectXVarOptional = {
    type: "dib-x-var-optional";
    id: string;
    data: {
        id: string;
        name: string;
        userValues: Record<string, unknown>;
    };
};

type DibObjectPointerXVar = {
    type: "pointer-x-var";
    id: string;
    data: {
        varId: string;
    };
};

type DibObjectXList = {
    type: "dib-x-list";
    id: string;
    data: {
        id: string;
        ofType: string;
        heading: string;
        varId: string;
        defaultCounter: number;
    };
    children?: string[];
};

type DibObjectXSection = {
    type: "dib-x-section";
    id: string;
    data: {
        id: string;
        name: string;
        heading: string;
        optional: boolean;
        autoCount: boolean;
        counterType: string;
        varId: string;
        defaultValue: boolean;
        varvalue: boolean;
        userValues: Record<string, unknown>;
    };
    children?: string[];
};

type DibObjectXAlternative = {
    type: "dib-x-alternative";
    id: string;
    data: {
        id: string;
        heading: string;
        varValue: string;
    };
    children?: string[];
};

type DibObjectXAlternatives = {
    type: "dib-x-alternatives";
    id: string;
    data: {
        id: string;
        heading: string;
        defaultValue: string;
        userValues: Record<string, unknown>;
    };
    children?: string[];
};

type DibObjectXComment = {
    type: "dib-x-comment";
    id: string;
    data: {
        id: string;
        heading: string;
    };
};

type DibObjectXOptional = {
    type: "dib-x-optional";
    id: string;
    data: {
        id: string;
        varId: string;
        name: string;
        userValues: Record<string, unknown>;
    };
    children?: string[];
};

export type DibJsonNode = {
    kind: string | null; // Must be a discriminator for the node type. Must dictates the shape of "object". In other words, we need X different kinds of DibJsonNode
    element: DibElement | null;
    object: DibObject | null;
    elementData: unknown | null;
};
/** A root object entry (rootObjects array) */
export interface DibRootObject {
    name: string;
    id: string;
    className: string;
}

/** A TOC node (toc map). Used in Part 2. */
export interface DibTocNode {
    attributes: Record<string, string>;
    children: {id: string}[];
    name?: string;
    type?: string;
}

/** Minimal document shape required by the render pipeline */
export interface RenderableDocument {
    elements: Record<string, DibElement>;
    objects: Record<string, DibObject>;
    elementdata: Record<string, unknown>;
    root: {id: string}[];
    topicId: string;
    id: string;
    resourceTypeId: string;
    segmentId: string;
    topictypeId: string;
    name: string;
}

/** Full document response from /dibappapi/api/documents/{resourceId} */
export interface DibDocument extends RenderableDocument {
    formatVersion: string;
    sourceFormat: string;
    eCount: number;
    version: string;
    selectId: string;
    supplierId: string;
    supplier: string;
    topictype: string;
    RowVersion: string;
    access: boolean;
    companylookup: boolean;
    language: string;
    origin: string;
    date?: string;
    rootObjects: DibRootObject[];
    descroot: {id: string}[];
    tocroot: {id: string}[];
    toc: Record<string, DibTocNode>;
    elementuserdata: Record<string, unknown>;
    viewroot: unknown[];
    showroot: unknown[];
    genroot: unknown[];
    variableroot: unknown[];
    genobjectroot: unknown[];
    relatedroot: string[];
    collectionroot: unknown[];
    defaultCollectionId: string;
    labels: unknown[];
    defaultLabel: unknown[];
}

export type RenderRule = {
    shouldParse: (
        node: DibJsonNode,
        map: RenderableDocument,
        options: RenderNodeOptions
    ) => boolean;
    parser: (
        node: DibJsonNode,
        options: RenderNodeOptions,
        data?: RenderNodeData,
        key?: string
    ) => ReactNode;
};

export type RenderAugmentation = {
    test: (node: DibJsonNode, options: RenderNodeOptions) => boolean;
    processor: (
        node: DibJsonNode,
        topicData: RenderableDocument,
        accumulator: ReactNode[]
    ) => ReactNode[];
};

export type RenderModifier = {
    name: string;
    test: (node: DibJsonNode, options: RenderNodeOptions) => boolean;
    modifier: (node: DibJsonNode, element: ReactElement) => ReactElement;
};

export type RenderNodeOptions = {
    rootTopicId?: string;
    skip?: (id: string) => boolean;
    isConditional?: (id: string) => boolean;
    isConditionalFocused?: (id: string) => boolean;
    replaceN?: boolean;
    n?: number;
    conditionalClassName?: string;
    CSSTarget?: string;
    skipAugmentations?: boolean;
};

export type RenderNodeData = {
    children?: ReactNode[];
    map?: RenderableDocument;
};

export type InlineRelationsData = {
    dataResourceId: string;
    id: string;
    name: string;
    publish: boolean;
    resourceTypeId: ResourceType;
    topicId: string;
    topictypeId: string;
    subname?: string;
    dataId?: string;
    dataSegmentId?: string;
    language?: string;
    supplierId?: string;
    categoryId?: string;
};

export type InlineRelationsObject = {
    type: "related";
    id: string;
    data: InlineRelationsData;
};

export type InlineRelationsResponse = {
    objects: {
        [key: string]: InlineRelationsObject;
    };
    related: string[];
};

export type ElementDataAccounting = {
    accounting?: string[];
    tax?: string[];
};

export type ElementDataTags = {
    tags?: string[];
};

export type ElementDataReferences = {
    references?: number;
};

export type ReferenceFlagsTo<T> = {
    reference: T;
    comment: T;
    translation: T;
    mirror: T;
};

export type DocumentPartsResponse = {parts: RenderableDocument[]};

// Order here is important to preserve legacy order
export const ReferenceFlags: ReferenceFlagsTo<number> = {
    reference: 0x001,
    comment: 0x008,
    translation: 0x004,
    mirror: 0x002,
};

// --- Checklist types ---

export enum ChecklistItemType {
    CheckItemParent = 0,
    CheckItem = 1,
    CheckItemInfo = 2,
}

export interface ChecklistMetadata {
    id: string;
    topicId: string;
    name: string;
    version: string;
    access: boolean;
    resourceTypeId: string;
    publish: number;
    supplierId: string;
    supplier: string;
    topictypeId: string;
    topictype: string;
    language: string;
    collectionId: string;
}

export interface ChecklistObject {
    objectType: string;
    id: string;
    name: string;
    groupId?: string;
    selected?: number;
    groupType?: number;
    children?: string[];
}

export interface ChecklistRelatedItem {
    objectType: string;
    topicTypeId: string;
    topicId: string;
    id?: string;
    resourceTypeId: ResourceType;
    version: string;
    name: string;
    description?: string;
    viewtype: string;
}

export interface ChecklistFileItem {
    objectType: string;
    id: string;
    topicId: string;
    subname: string;
}

export interface ChecklistTocElement {
    children?: string[];
    title?: string;
    root?: boolean;
    files?: ChecklistFileItem[];
    show?: string[];
    view?: string[];
    type: ChecklistItemType.CheckItem | ChecklistItemType.CheckItemInfo;
    checkItemType: ChecklistItemType;
    info?: boolean;
    ingress?: string;
    description?: string;
    law?: string;
    related?: ChecklistRelatedItem[];
    viewByGroup?: Record<string, string[]>;
    showByGroup?: Record<string, string[]>;
}

export interface ChecklistToc {
    [key: string]: ChecklistTocElement;
}

export interface ChecklistLabel {
    id: string;
    name: string;
}

export interface LabelGroup {
    id: string;
    name: string;
    labels: ChecklistLabel[];
}

export interface ChecklistElement {
    name: string;
    children: ({text: string} | {id: string})[];
    attributes?: Record<string, string>;
}

export interface ChecklistElements {
    [key: string]: ChecklistElement;
}

export interface ChecklistObjects {
    [key: string]: ChecklistObject;
}

export interface ChecklistLinkObject {
    type: "linkdata";
    id: string;
    data: {
        topicId: string;
        type: string;
        resourceTypeId: number;
        viewType: number;
        topicTypeId: number;
        topicName: string;
    };
}

export interface ChecklistData {
    metadata: ChecklistMetadata;
    toc: ChecklistToc;
    objects: ChecklistObjects;
    elements: ChecklistElements;
    viewroot: string[];
    showroot: string[];
    labels: string[];
    defaultLabels: string[];
    viewGroups: LabelGroup[];
    showGroups: LabelGroup[];
    defaultCollectionId: string;
    rootNodeId: string;
    tocParentMap: Record<string, string>;
    infoIds: string[];
    leafNodeIds: string[];
}

export interface RawChecklistResponse {
    id: string;
    topicId: string;
    name: string;
    version: string;
    access: boolean;
    resourceTypeId: string;
    publish: number;
    supplierId: string;
    supplier: string;
    topictypeId: string;
    topictype: string;
    language: string;
    collectionId: string;
    toc: Record<string, ChecklistTocElement>;
    objects: Record<string, ChecklistObject>;
    elements: Record<string, ChecklistElement>;
    viewroot: string[];
    showroot: string[];
    labels: unknown[];
    defaultLabels: string[];
    defaultCollectionId: string;
}

// --- Collection & Status types ---

export enum Status {
    deviation = 3,
    na = 2,
    ok = 1,
    todo = 0,
}

export enum DataStatus {
    deviation = "DEVIATION",
    na = "NA",
    ok = "OK",
    todo = "TODO",
    withComments = "WITH_COMMENTS",
}

export const StatusToDataStatus: Record<Status, DataStatus> = {
    [Status.deviation]: DataStatus.deviation,
    [Status.na]: DataStatus.na,
    [Status.ok]: DataStatus.ok,
    [Status.todo]: DataStatus.todo,
};

export const WITH_COMMENTS = "WITH_COMMENTS";

export const DataStatusToStatus: Record<
    DataStatus,
    Status | typeof WITH_COMMENTS
> = {
    [DataStatus.deviation]: Status.deviation,
    [DataStatus.na]: Status.na,
    [DataStatus.ok]: Status.ok,
    [DataStatus.todo]: Status.todo,
    [DataStatus.withComments]: WITH_COMMENTS,
};

export interface CollectionMetadata {
    collectionId: string;
    name: string;
    draft: 0 | 1;
    owner: string;
    isOwner: 0 | 1;
    created: string;
    lastEdit: string;
}

export interface CollectionCheckItem {
    collectionId: string;
    id: string;
    status: Status;
    comment?: string;
    editDate?: string;
}

export interface CollectionLabel {
    collectionId: string;
    groupId: string;
    labelId: string;
}

export interface CollectionCollaborator {
    id: string;
    name: string;
    email?: string;
}

export interface CollectionDocument {
    id: string;
    topicId: string;
    selected: boolean;
}

export interface CollectionDataResponse {
    metadata?: CollectionMetadata;
    labels: CollectionLabel[];
    collaborators: CollectionCollaborator[];
    checkitems: Record<string, CollectionCheckItem>;
    documents: CollectionDocument[];
    access: boolean;
}

export interface CommentsElementData {
    ci_status: number;
    ci_comment: number;
    ci_editDate: string;
    ci_comments: string[];
}

export interface CommentsResponse {
    elementdata: Record<string, CommentsElementData>;
    objects: Record<
        string,
        {
            type: string;
            id: string;
            data: {
                id: string;
                comment: string;
                editedBy: string;
                editDate: string;
                epost: string;
            };
        }
    >;
}

export interface ChecklistFilterGroup {
    name: string;
    labels: {name: string; id: string}[];
}

export interface GetChecklistPartElementsArgs {
    resourceId: string;
    id: string;
    recursive?: boolean;
    isPreview?: boolean;
}

export interface ChecklistPartElements {
    elements: ChecklistElements;
    objects: ChecklistObjects;
}

export interface TopicCollection {
    type: "collection";
    id: string;
    data: {
        id: string;
        name: string;
        created: string;
        lastEdit: string;
        draft: 0 | 1;
    };
}

// ─── Document Generator Types ────────────────────────────────────

/** Response from GET /dibapi/api/document (with collectionId param for docgen) */
export interface DocgenDocumentResponse {
    topicData: DocgenTopicData;
    metadata: Record<string, any>;
}

export interface DocgenTopicData extends DibDocument {
    /**
     * dokumentgenerator's render-prop callbacks index `objects`/`elements`
     * loosely (their own callback props are untyped `any`), so we deliberately
     * keep these wider than `DibDocument`'s `Record<string, DibObject>` /
     * `Record<string, DibElement>`. Everything else — `descroot`, `relatedroot`,
     * `supplier`, `date`, etc. — is inherited from `DibDocument` (same backend
     * URL as `getDocument`, so the runtime payload carries them).
     */
    objects: Record<string, any>;
    elements: Record<string, any>;
}

/** Response from GET /dibapi/api/conditions/{resourceId} */
export interface ResourceConditionsResponse {
    operands: Record<string, any>;
    conditions: Record<string, any>;
    condition_label_groups: Record<
        string,
        {
            label_group_id: string;
            label_group_name: string;
            view_title?: string;
            children: string[];
        }
    >;
    condition_labels: Record<
        string,
        {
            label_id: string;
            label_name: string;
        }
    >;
}

/** Items from GET /dibapi/api/condition-label-data/{topicId}/{collectionId} */
export type ResourceConditionUserData = ResourceConditionUserDataItem[];
export interface ResourceConditionUserDataItem {
    topic_id: string;
    collection_id: string;
    label_group: string;
    label: string;
}

/** Args for patchXVariable mutation */
export interface PatchXVariableArgs {
    topicId: string;
    collectionId?: string;
    component: string;
    id: string;
    value: string;
    n?: string;
}
